import Board from "../../chessBoard/components/Board";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSettings } from "@/redux/settings";
import { Chess } from "@chesshotel/chess";
import type { Move } from "@chesshotel/chess";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaFastBackward,
  FaFastForward,
  FaStepBackward,
  FaStepForward,
} from "react-icons/fa";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
const MAX_VISIBLE_FULL_MOVES = 4;
const MOVE_ROW_HEIGHT_PX = 28;
const DEFAULT_BOARD_SIZE = 420;

type ParseResult = {
  startFen: string;
  moves: Move[];
  error: string | null;
};

type SimpleGameViewerProps = {
  pgn: string;
  playingWhite?: boolean;
  boardSize?: number;
  caption?: string;
};

function parsePgn(pgn: string): ParseResult {
  if (!pgn?.trim()) {
    return {
      startFen: START_FEN,
      moves: [],
      error: null,
    };
  }

  try {
    const chess = new Chess();
    chess.loadPgn(pgn);
    const moves = chess.history({ verbose: true });
    const startFen = moves.length > 0 ? moves[0].before : chess.fen();

    return {
      startFen,
      moves,
      error: null,
    };
  } catch (error) {
    return {
      startFen: START_FEN,
      moves: [],
      error:
        error instanceof Error ? error.message : "Could not parse PGN input.",
    };
  }
}

export default function SimpleGameViewer({
  pgn,
  playingWhite = true,
  boardSize,
  caption,
}: SimpleGameViewerProps) {
  const settings = useSettings();
  const parsedGame = useMemo(() => parsePgn(pgn), [pgn]);
  const [positionIndex, setPositionIndex] = useState(0);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);
  const [isDesktopLayout, setIsDesktopLayout] = useState(false);
  const resolvedBoardSize = boardSize ?? DEFAULT_BOARD_SIZE;
  const [effectiveBoardSize, setEffectiveBoardSize] =
    useState(resolvedBoardSize);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const moveListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setHasUserNavigated(false);
    setPositionIndex(parsedGame.moves.length);
    moveListRef.current?.scrollTo({ top: 0 });
  }, [parsedGame.moves.length]);

  useEffect(() => {
    if (!containerRef.current || typeof window === "undefined") {
      return;
    }

    const updateBoardSize = () => {
      if (!containerRef.current) {
        return;
      }

      const containerWidth = containerRef.current.clientWidth;
      if (containerWidth === 0) {
        return;
      }

      const isDesktopLayout = window.matchMedia("(min-width: 768px)").matches;
      setIsDesktopLayout((prev) =>
        prev === isDesktopLayout ? prev : isDesktopLayout,
      );
      const nextSize = isDesktopLayout
        ? Math.min(resolvedBoardSize, containerWidth)
        : containerWidth;
      setEffectiveBoardSize((prev) => (prev === nextSize ? prev : nextSize));
    };

    updateBoardSize();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateBoardSize);
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateBoardSize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateBoardSize);
    };
  }, [resolvedBoardSize]);

  const safePositionIndex = Math.max(
    0,
    Math.min(positionIndex, parsedGame.moves.length),
  );

  const currentFen =
    safePositionIndex === 0
      ? parsedGame.startFen
      : parsedGame.moves[safePositionIndex - 1]?.after || parsedGame.startFen;

  const lastMove =
    safePositionIndex > 0 ? parsedGame.moves[safePositionIndex - 1] : null;
  const moveListMaxHeight = MAX_VISIBLE_FULL_MOVES * MOVE_ROW_HEIGHT_PX;

  function goToPosition(nextPositionIndex: number) {
    setHasUserNavigated(true);
    const clampedIndex = Math.max(
      0,
      Math.min(nextPositionIndex, parsedGame.moves.length),
    );
    setPositionIndex(clampedIndex);
  }

  useEffect(() => {
    if (!hasUserNavigated) {
      return;
    }

    if (!moveListRef.current) {
      return;
    }

    if (safePositionIndex === 0) {
      moveListRef.current.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const selectedMove = moveListRef.current.querySelector<HTMLElement>(
      `[data-move-index="${safePositionIndex}"]`,
    );

    if (!selectedMove) {
      return;
    }

    const container = moveListRef.current;
    const moveTop = selectedMove.offsetTop;
    const moveBottom = moveTop + selectedMove.offsetHeight;
    const viewportTop = container.scrollTop;
    const viewportBottom = viewportTop + container.clientHeight;
    const padding = 6;

    if (moveTop < viewportTop) {
      container.scrollTo({
        top: Math.max(0, moveTop - padding),
        behavior: "smooth",
      });
      return;
    }

    if (moveBottom > viewportBottom) {
      container.scrollTo({
        top: moveBottom - container.clientHeight + padding,
        behavior: "smooth",
      });
    }
  }, [safePositionIndex, hasUserNavigated]);

  function renderMoves() {
    if (parsedGame.moves.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="py-4 text-center text-gray-500">
            No moves available
          </td>
        </tr>
      );
    }

    const rows = [];
    let moveNumber = 1;

    for (let i = 0; i < parsedGame.moves.length; i += 2) {
      const whiteMove = parsedGame.moves[i];
      const blackMove = parsedGame.moves[i + 1];
      const whiteIndex = i + 1;
      const blackIndex = i + 2;

      rows.push(
        <tr className="select-none even:bg-white" key={`${moveNumber}-${i}`}>
          <td className="py-0.5 pl-3">{moveNumber}</td>
          <td
            className="cursor-pointer"
            onClick={() => goToPosition(whiteIndex)}
            data-move-index={whiteIndex}
          >
            <div
              className={cn(
                "ml-3 rounded-lg font-semibold px-1 py-0.5",
                safePositionIndex === whiteIndex && "text-orange-400",
              )}
            >
              {whiteMove.san}
            </div>
          </td>
          <td
            className={cn("cursor-pointer", !blackMove && "cursor-default")}
            onClick={() => blackMove && goToPosition(blackIndex)}
            data-move-index={blackMove ? blackIndex : undefined}
          >
            <div
              className={cn(
                "ml-3 rounded font-semibold px-1 py-0.5",
                safePositionIndex === blackIndex && "text-orange-400",
              )}
            >
              {blackMove?.san || ""}
            </div>
          </td>
        </tr>,
      );

      moveNumber++;
    }

    return rows;
  }

  return (
    <div ref={containerRef} className="flex flex-col items-start w-full">
      <div className="flex flex-col w-full md:w-auto md:flex-row">
        <div className="flex self-start justify-center rounded-t-md bg-athens-gray-100 w-full md:w-fit md:rounded-l-md md:rounded-tr-none">
          <Board
            fen={currentFen}
            animate={true}
            size={effectiveBoardSize}
            playingWhite={playingWhite}
            possibleMoves={{}}
            onMoveMade={() => {}}
            settings={settings}
            lastMove={lastMove}
            showLastMove={true}
          />
        </div>
        <div
          className="flex flex-col items-stretch w-full p-4 rounded-b-md md:rounded-r-md md:rounded-bl-none md:min-w-[260px] bg-athens-gray-100"
          style={{ maxHeight: effectiveBoardSize + 44 + "px" }}
        >
          <div className="flex flex-col justify-between min-h-0 md:flex-grow">
            {parsedGame.error && (
              <div className="mb-3 text-sm text-red-600">
                Invalid PGN: {parsedGame.error}
              </div>
            )}
            <div
              ref={moveListRef}
              className="overflow-y-auto md:flex-1"
              style={
                isDesktopLayout
                  ? undefined
                  : { maxHeight: `${moveListMaxHeight}px` }
              }
            >
              <table className="w-full my-0 text-sm text-gray-500">
                <colgroup>
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "38%" }} />
                  <col style={{ width: "38%" }} />
                </colgroup>
                <tbody>{renderMoves()}</tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-center gap-1 px-5 py-4">
            <Button
              variant="secondary"
              className="bg-athens-gray-150 hover:bg-athens-gray-200"
              disabled={safePositionIndex === 0}
              onClick={() => goToPosition(0)}
            >
              <FaFastBackward />
            </Button>
            <Button
              className="bg-athens-gray-150 hover:bg-athens-gray-200"
              variant="secondary"
              disabled={safePositionIndex === 0}
              onClick={() => goToPosition(safePositionIndex - 1)}
            >
              <FaStepBackward />
            </Button>
            <Button
              className="bg-athens-gray-150 hover:bg-athens-gray-200"
              variant="secondary"
              disabled={safePositionIndex === parsedGame.moves.length}
              onClick={() => goToPosition(safePositionIndex + 1)}
            >
              <FaStepForward />
            </Button>
            <Button
              variant="secondary"
              className="bg-athens-gray-150 hover:bg-athens-gray-200"
              disabled={safePositionIndex === parsedGame.moves.length}
              onClick={() => goToPosition(parsedGame.moves.length)}
            >
              <FaFastForward />
            </Button>
          </div>
        </div>
      </div>
      {caption && (
        <p className="w-full mt-2 text-sm italic leading-relaxed text-left text-gray-500">
          {caption}
        </p>
      )}
    </div>
  );
}
